import { useState, useEffect, useRef, createContext, useContext } from 'react';
import { db, SYNC_STATUS } from '../db/shiksha-db';
import { useNetworkStatus } from './useNetworkStatus';
import { auth } from '../services/firebase';

const WORKER_API_URL =
  import.meta.env.VITE_WORKER_API_URL || 'https://shiksha-edge-api.workers.dev';

// ─── Promise Queue for DB Operations ──────────────────────
// This guarantees that no two sync operations attempt to
// run overlapping Dexie transactions.
const dbQueueRef = { current: Promise.resolve() };
const enqueueDbTask = (taskFn) => {
  dbQueueRef.current = dbQueueRef.current.then(async () => {
    try {
      await taskFn();
    } catch (e) {
      console.error('[ShikshaSync] DB Task Error:', e);
    }
  });
};

// ─── The actual engine (runs exactly once) ──────────────
function useOfflineSyncEngine() {
  const isOnline = useNetworkStatus();
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);

  // Refs to fully decouple from React render cycle
  const isSyncingRef = useRef(false);
  const isPullingRef = useRef(false);
  const isOnlineRef = useRef(isOnline);
  const mountedRef = useRef(true);

  useEffect(() => { isOnlineRef.current = isOnline; }, [isOnline]);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  // ── Pull master data on mount + every 5 min ──
  useEffect(() => {
    async function pullMasterData() {
      if (!isOnlineRef.current || isPullingRef.current) return;
      isPullingRef.current = true;

      let idToken = null;
      try {
        if (auth.currentUser) idToken = await auth.currentUser.getIdToken(false);
      } catch { isPullingRef.current = false; return; }
      if (!idToken) { isPullingRef.current = false; return; }

      try {
        const res = await fetch(`${WORKER_API_URL}/api/sync/poll`, {
          headers: { Authorization: `Bearer ${idToken}` }
        });
        if (!res.ok) return;

        const data = await res.json();

        // Parse Students
        const parsedStudents = [];
        const apiStudents = data.students || [];
        if (apiStudents.length > 1) {
          apiStudents.slice(1).forEach((row) => {
            const rollNo = parseInt(row[0]) || 0;
            const className = (row[2] || '').trim();
            const section = (row[3] || 'A').trim();
            const stableId = row[8] || `${rollNo}_${className}_${section}`.replace(/[^a-zA-Z0-9_]/g, '_');
            parsedStudents.push({
              id: stableId, rollNo, name: row[1] || '', class: className, section,
              guardianName: row[4] || '', dob: row[5] || '', gender: row[6] || '', motherName: row[7] || '',
            });
          });
        }

        // Parse Marks
        const marksMap = new Map();
        const apiMarks = data.marks || [];
        if (apiMarks.length > 1) {
          apiMarks.slice(1).forEach((row) => {
            const sId = row[0] ? row[0].toString().trim() : '';
            const subject = row[1] || '', term = row[2] || '', componentId = row[3] || '';
            const score = Number(row[4]) || 0, maxScore = Number(row[5]) || 0;
            const compositeId = `mark_${sId}_${subject}_${term}_${componentId}`.replace(/[^a-zA-Z0-9_]/g, '_');
            if (score === -1) marksMap.delete(compositeId);
            else marksMap.set(compositeId, { id: compositeId, studentId: sId, subject, term, componentId, score, maxScore, syncStatus: SYNC_STATUS.SYNCED });
          });
        }

        // Parse Attendance
        const attendanceMap = new Map();
        const apiAttendance = data.attendance || [];
        if (apiAttendance.length > 1) {
          apiAttendance.slice(1).forEach((row) => {
            const sId = row[0] ? row[0].toString().trim() : '';
            const date = row[1] || '', status = row[2] || '';
            const compositeId = `att_${sId}_${date}`.replace(/[^a-zA-Z0-9_]/g, '_');
            if (status === 'DELETED') attendanceMap.delete(compositeId);
            else attendanceMap.set(compositeId, { id: compositeId, studentId: sId, date, status, syncStatus: SYNC_STATUS.SYNCED });
          });
        }

        // Parse Schemas
        const parsedSchemas = [];
        const apiSchemas = data.schemas || [];
        if (apiSchemas.length > 1) {
          apiSchemas.slice(1).forEach((row, i) => {
            parsedSchemas.push({ id: i + 1, cohort: row[0] || '', xml: row[1] || '', code: row[2] || '', updatedAt: Date.now() });
          });
        }

        // Parse Teachers
        const teachersMap = new Map();
        const apiTeachers = data.teachers || [];
        if (apiTeachers.length > 1) {
          apiTeachers.slice(1).forEach((row) => {
            const uid = row[0] || '', status = row[8] || 'ACTIVE';
            if (!uid) return;
            if (status === 'DELETED') { teachersMap.delete(uid); return; }
            let subjectsAssigned = [];
            try { subjectsAssigned = JSON.parse(row[7] || '[]'); } catch { subjectsAssigned = []; }
            teachersMap.set(uid, {
              uid, email: row[1] || '', name: row[2] || '', role: row[3] || 'teacher',
              isClassTeacher: row[4] === 'TRUE', classTeacherOf: row[5] || '', classTeacherSection: row[6] || '',
              subjectsAssigned,
            });
          });
        }

        const parsedMarks = Array.from(marksMap.values());
        const parsedAttendance = Array.from(attendanceMap.values());
        const parsedTeachers = Array.from(teachersMap.values());

        enqueueDbTask(async () => {
          // Write tables (each in isolated try/catch to prevent cascading)
          try {
            const ps = await db.syncQueue.where({ table: 'students' }).count();
            if (ps === 0 && parsedStudents.length > 0) {
              await db.transaction('rw', db.students, async () => { await db.students.clear(); await db.students.bulkAdd(parsedStudents); });
            }
          } catch (e) { console.warn('[Sync] Students write:', e.message); }

          try {
            const pm = await db.syncQueue.where({ table: 'marks' }).count();
            if (pm === 0 && parsedMarks.length > 0) {
              await db.transaction('rw', db.marks, async () => { await db.marks.clear(); await db.marks.bulkPut(parsedMarks); });
            }
          } catch (e) { console.warn('[Sync] Marks write:', e.message); }

          try {
            const pa = await db.syncQueue.where({ table: 'attendance' }).count();
            if (pa === 0 && parsedAttendance.length > 0) {
              await db.transaction('rw', db.attendance, async () => { await db.attendance.clear(); await db.attendance.bulkPut(parsedAttendance); });
            }
          } catch (e) { console.warn('[Sync] Attendance write:', e.message); }

          try {
            const psc = await db.syncQueue.where({ table: 'schemas' }).count();
            if (psc === 0 && parsedSchemas.length > 0) {
              await db.transaction('rw', db.schemas, async () => { await db.schemas.clear(); await db.schemas.bulkPut(parsedSchemas); });
            }
          } catch (e) { console.warn('[Sync] Schemas write:', e.message); }

          try {
            if (parsedTeachers.length > 0) {
              await db.transaction('rw', db.teachers, async () => { await db.teachers.clear(); await db.teachers.bulkPut(parsedTeachers); });
            }
          } catch (e) { console.warn('[Sync] Teachers write:', e.message); }
        });

      } catch (e) {
        console.error('[ShikshaSync] Polling failed', e);
      } finally {
        isPullingRef.current = false;
      }
    }

    pullMasterData();
    const interval = setInterval(pullMasterData, 300000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Drain sync queue on mount + every 30 seconds ──
  useEffect(() => {
    async function drainSyncQueue() {
      if (!isOnlineRef.current || isSyncingRef.current) return;

      let idToken = null;
      try {
        if (auth.currentUser) idToken = await auth.currentUser.getIdToken(false);
      } catch { return; }
      if (!idToken) return;

      isSyncingRef.current = true;
      if (mountedRef.current) setIsSyncing(true);

      enqueueDbTask(async () => {
        try {
          const items = await db.syncQueue.toArray();
          if (items.length === 0) return;

          let response = await fetch(`${WORKER_API_URL}/api/sync`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
            body: JSON.stringify({ operations: items }),
          });

          if (response.status === 401) {
            try {
              idToken = await auth.currentUser.getIdToken(true);
              response = await fetch(`${WORKER_API_URL}/api/sync`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
                body: JSON.stringify({ operations: items }),
              });
            } catch (e) {
              console.error('[ShikshaSync] Token refresh failed:', e);
              return;
            }
          }

          if (response.status === 409) {
            alert('A new version of the app is required. The app will now reload.');
            window.location.reload(true);
            return;
          }

          if (response.ok) {
            const result = await response.json();
            if (!result || !Array.isArray(result.synced)) throw new Error('Malformed response');

            const syncedIds = result.synced;
            if (syncedIds.length > 0) await db.syncQueue.bulkDelete(syncedIds);

            const failedItems = items.filter(i => !syncedIds.includes(i.id));
            for (const item of failedItems) {
              const retryCount = (item.retryCount || 0) + 1;
              if (retryCount >= 3) {
                await db.syncQueue.delete(item.id);
                try { await db.table(item.table).update(item.recordId, { syncStatus: SYNC_STATUS.CONFLICT }); } catch {}
              } else {
                await db.syncQueue.update(item.id, { retryCount });
              }
            }

            for (const item of items) {
              if (syncedIds.includes(item.id)) {
                try { await db.table(item.table).update(item.recordId, { syncStatus: SYNC_STATUS.SYNCED }); } catch {}
              }
            }

            if (syncedIds.length > 0) {
              try { await db.activityLog.add({ action: 'sync', details: `Synced ${syncedIds.length} records`, timestamp: Date.now() }); } catch {}
            }
          } else {
            console.error('[ShikshaSync] HTTP', response.status);
          }
        } catch (error) {
          console.error('[ShikshaSync] Sync failed:', error);
        } finally {
          isSyncingRef.current = false;
          if (mountedRef.current) {
            setIsSyncing(false);
            try { const c = await db.syncQueue.count(); if (mountedRef.current) setPendingSyncCount(c); } catch {}
          }
        }
      });
    }

    drainSyncQueue();
    const interval = setInterval(drainSyncQueue, 30000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Sync when coming back online ──
  useEffect(() => {
    const handleOnline = () => {
      if (isSyncingRef.current) return;
      setTimeout(async () => {
        if (isSyncingRef.current) return;
        isSyncingRef.current = true;
        if (mountedRef.current) setIsSyncing(true);

        enqueueDbTask(async () => {
          try {
            let idToken = null;
            if (auth.currentUser) idToken = await auth.currentUser.getIdToken(false);
            if (!idToken) return;
            const items = await db.syncQueue.toArray();
            if (items.length === 0) return;
            const response = await fetch(`${WORKER_API_URL}/api/sync`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
              body: JSON.stringify({ operations: items }),
            });
            if (response.ok) {
              const result = await response.json();
              if (result && Array.isArray(result.synced) && result.synced.length > 0) {
                await db.syncQueue.bulkDelete(result.synced);
              }
            }
          } catch (e) {
            console.error('[ShikshaSync] Online sync failed:', e);
          } finally {
            isSyncingRef.current = false;
            if (mountedRef.current) {
              setIsSyncing(false);
              try { const c = await db.syncQueue.count(); if (mountedRef.current) setPendingSyncCount(c); } catch {}
            }
          }
        });
      }, 2000);
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, []);

  // Expose a manual trigger and refresh
  const triggerSync = async () => {
    try { const c = await db.syncQueue.count(); if (mountedRef.current) setPendingSyncCount(c); } catch {}
  };
  const refreshPendingCount = triggerSync;

  return { isOnline, pendingSyncCount, isSyncing, triggerSync, refreshPendingCount };
}

const OfflineSyncContext = createContext();

export function OfflineSyncProvider({ children }) {
  const syncState = useOfflineSyncEngine();
  return (
    <OfflineSyncContext.Provider value={syncState}>
      {children}
    </OfflineSyncContext.Provider>
  );
}

export function useOfflineSync() {
  const context = useContext(OfflineSyncContext);
  if (context === undefined) {
    throw new Error('useOfflineSync must be used within an OfflineSyncProvider');
  }
  return context;
}

